import { useState } from 'react';
import { useLoaded } from '../lib/useLoaded.ts';
import { LoadingNotice, EmptyNotice, ErrorNotice } from '../components/StateNotice.tsx';
import type { PlanPendiente } from '../lib/contracts.ts';
import type { DataPort } from '../adapters/port.ts';
import { resolverPlan, generarEntregable } from '../adapters/http.ts';

/**
 * Aprobaciones — los planes que esperan tu OK, con el botón que los lanza.
 *
 * Antes esta pantalla mostraba «pendiente de cablear» sobre un circuito que ya
 * existía: el flujo de aprobación vivía sólo en el acuse del Brief. Ahora lee
 * `ncos_planes_pendientes` y aprueba contra `/api/plan`, el mismo endpoint.
 *
 * 🔑 Nada se ejecuta y no hay fecha hasta que aprobás. El reloj del SLA arranca
 * en el momento en que apretás el botón, no antes.
 */
export function Approvals({ port }: { port: DataPort }) {
  const planes = useLoaded(() => port.pendingPlans());
  // Decisiones tomadas en esta sesión, para sacar la tarjeta de la lista sin
  // recargar toda la pantalla.
  const [resueltos, setResueltos] = useState<Record<string, { estado: string; hasta: string | null }>>({});

  const pendientes = planes.kind === 'ok'
    ? planes.value.filter((p) => !resueltos[p.proyecto])
    : [];

  return (
    <section>
      <p className="lede">
        {planes.kind !== 'ok'
          ? 'Nada se ejecuta sin tu OK explícito. Cada decisión queda registrada con fecha y responsable.'
          : `${pendientes.length === 0 ? 'Ningún plan espera' : pendientes.length === 1 ? '1 plan espera' : `${pendientes.length} planes esperan`} tu aprobación. Nada arranca —ni corre el reloj— hasta que aprobás.`}
      </p>

      {planes.kind === 'loading' && <LoadingNotice what="los planes" />}
      {planes.kind === 'error' && <ErrorNotice message={planes.message} />}
      {planes.kind === 'empty' && <EmptyNotice what="planes por aprobar" />}
      {planes.kind === 'ok' && pendientes.length === 0 && Object.keys(resueltos).length > 0 && (
        <EmptyNotice what="planes por aprobar" />
      )}

      {planes.kind === 'ok' && (
        <div className="rows">
          {pendientes.map((p) => (
            <PlanCard key={p.proyecto} p={p}
              onResuelto={(r) => setResueltos((prev) => ({ ...prev, [p.proyecto]: r }))} />
          ))}
        </div>
      )}
    </section>
  );
}

function PlanCard({ p, onResuelto }: {
  p: PlanPendiente;
  onResuelto: (r: { estado: string; hasta: string | null }) => void;
}) {
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Lo que se muestra puede cambiar: si rechazás el plan completo, el sistema
  // devuelve una contrapropuesta reducida y la tarjeta pasa a mostrar ESA, con
  // los botones de nuevo. No desaparece: es una segunda oferta, no un cierre.
  const [vista, setVista] = useState<VistaPlan>({
    entregable: p.entregable, nivel: p.nivel, complejidad: p.complejidad,
    plataformas: p.plataformas, minutos: p.minutos, justificacion: p.justificacion,
    alcance: null, roadmap: p.roadmap, reducido: false,
  });

  async function decidir(decision: 'aprobar' | 'rechazar') {
    if (enviando) return;
    setEnviando(true); setError(null);
    const r = await resolverPlan(p.proyecto, decision);
    setEnviando(false);
    if (typeof r === 'string') { setError(r); return; }
    // Contrapropuesta: se reemplaza lo que muestra la tarjeta y sigue abierta.
    if (r.estado === 'contrapropuesta' && r.plan) {
      setVista({
        entregable: r.plan.entregable, nivel: r.plan.nivel, complejidad: r.plan.complejidad,
        plataformas: r.plan.plataformas, minutos: r.plan.minutos, justificacion: r.plan.justificacion,
        alcance: r.plan.alcance, roadmap: r.plan.roadmap.map((t) => ({ label: t.label, min: t.max })),
        reducido: true,
      });
      return;
    }
    // Al aprobar, el motor arranca solo: la pieza aparece en Biblioteca.
    if (r.estado === 'plan_aprobado') void generarEntregable(p.proyecto);
    // Aprobado o rechazo final: sale de la lista.
    onResuelto({ estado: r.estado, hasta: r.hasta });
  }

  const extra = Math.max(0, vista.minutos - 60);

  return (
    <article className="card-approval card-approval--warn">
      <div className="card-approval__head">
        <span className="card-approval__id mono">{p.proyecto}</span>
        <span className="card-approval__title">{vista.entregable}</span>
        <span className="chip-state chip-state--warn">
          {vista.reducido ? 'Contrapropuesta · tu decisión' : 'Espera tu aprobación'}
        </span>
      </div>

      <p className="card-approval__detail">{p.titulo}</p>

      {vista.reducido && (
        <p className="acuse__resuelto" role="status">
          Rechazaste el plazo del plan completo. Esta es una versión <strong>reducida</strong>:
          menos entregables y menor complejidad, para que entre en menos tiempo. Aceptala o no.
        </p>
      )}

      <dl className="acuse__pacto">
        <div><dt>Qué recibís</dt><dd><strong>{vista.entregable}</strong> — {vista.nivel}</dd></div>
        <div><dt>Complejidad</dt><dd>{vista.complejidad}</dd></div>
        <div><dt>Plataformas</dt><dd>{vista.plataformas.join(' · ')}</dd></div>
        <div><dt>Duración</dt><dd><strong>{vista.minutos} minutos</strong> desde que aprobás
          {extra > 0 && ` (${extra} min por encima de la hora base)`}</dd></div>
        {vista.reducido && vista.alcance && (
          <div className="acuse__motivo"><dt>Qué se saca respecto del plan completo</dt><dd>{vista.alcance}</dd></div>
        )}
        {!vista.reducido && vista.justificacion && (
          <div className="acuse__motivo"><dt>Por qué pide más de 1 hora</dt><dd>{vista.justificacion}</dd></div>
        )}
      </dl>

      {vista.roadmap.length > 0 && (
        <ol className="acuse__roadmap">
          {vista.roadmap.map((t, i) => (
            <li key={i}><span className="acuse__paso">{t.label}</span><span className="acuse__min">{t.min} min</span></li>
          ))}
        </ol>
      )}

      <div className="acuse__decision">
        <button type="button" className="btn-primary" disabled={enviando}
          onClick={() => void decidir('aprobar')}>
          {enviando ? 'Registrando…' : vista.reducido ? 'Apruebo esta versión — arrancá' : 'Apruebo el plan — arrancá'}
        </button>
        <button type="button" className="btn-ghost" disabled={enviando}
          onClick={() => void decidir('rechazar')}>
          {vista.reducido ? 'Tampoco — cerralo' : 'No apruebo — proponé menos'}
        </button>
      </div>

      {error && <p className="notice notice--error" role="alert">{error}</p>}
    </article>
  );
}

interface VistaPlan {
  entregable: string;
  nivel: string;
  complejidad: 'baja' | 'media' | 'alta';
  plataformas: readonly string[];
  minutos: number;
  justificacion: string | null;
  alcance: string | null;
  roadmap: ReadonlyArray<{ label: string; min: number }>;
  reducido: boolean;
}
